import dayjs, { Dayjs } from "dayjs";
import { useEffect, useRef, useState } from "react";

import { DateTimePickerInput } from "../../../../components/DateTimePicker";
import { validateDateAnswer } from "../../../../utils/customFields";
import {
  DATE_VALUE_FORMAT,
  getDateFormatForCountry,
} from "../../../../utils/portalDateFormat";
import { ErrorMessage } from "./AddPatientModal.styles";

type CustomFieldDateInputProps = {
  // Base input name, used as the input's id.
  name: string;
  // The stored answer, in the server format (YYYY-MM-DD).
  value?: string;
  isRequired: boolean;
  errorMessage?: string;
  // "US" renders MM/DD/YYYY; every other portal country renders DD/MM/YYYY.
  countryCode?: string;
  onChange: (value: string) => void;
};

// EN-5067: a Date custom field is entered exactly like the Date of birth field above it, while
// still emitting the server format.
// EN-5272: both are now the portal's date picker rather than three DD / MM / YYYY segments. The
// picker only commits a real date, so the value is always complete or "". Unlike the Date of birth,
// future dates stay selectable — a custom field can legitimately be an expiry or review date.
export function CustomFieldDateInput({
  name,
  value,
  isRequired,
  errorMessage,
  countryCode,
  onChange,
}: CustomFieldDateInputProps) {
  // Add Patient clears a custom field's error as soon as it is edited, but the Date of birth field
  // keeps its message live once touched. Mirror that: after the form has flagged this field, re-run
  // the same rules locally on each change instead of going quiet until re-submit.
  const [liveMessage, setLiveMessage] = useState("");
  const wasFlagged = useRef(false);

  useEffect(() => {
    if (errorMessage) {
      wasFlagged.current = true;
      setLiveMessage(errorMessage);
    }
  }, [errorMessage]);

  const format = getDateFormatForCountry(countryCode);
  const parsed = value ? dayjs(value, DATE_VALUE_FORMAT, true) : null;

  // Clearing hands back null despite the signature, hence the guard.
  const handleChange = (next: Dayjs | null) => {
    const nextValue = next?.isValid() ? next.format(DATE_VALUE_FORMAT) : "";
    onChange(nextValue);

    if (wasFlagged.current) {
      setLiveMessage(validateDateAnswer(nextValue, isRequired));
    }
  };

  return (
    <>
      <DateTimePickerInput
        id={name}
        showTime={false}
        format={format}
        placeholder={format}
        // The width of the Add patient fields around it.
        width="248px"
        value={parsed?.isValid() ? parsed : null}
        onChange={handleChange}
      />
      <ErrorMessage>
        {wasFlagged.current ? liveMessage : errorMessage}
      </ErrorMessage>
    </>
  );
}
